import * as Api from "@/api";
import * as Billing from "@/models/billing";
import * as Pages from "@/components/Pages";
import * as Paper from "@/components/PaperContainer";
import * as React from "react";

import Forms from "@/components/Forms";

import { OperatelyLogo } from "@/components/OperatelyLogo";
import { TosAndPrivacyPolicy } from "@/features/auth/AgreeToTosAndPp";
import { PasswordStrength } from "@/features/auth/PasswordStrength";
import { validatePassword } from "@/features/auth/validatePassword";

import { useFieldValue } from "@/components/Forms/FormContext";
import { ErrorMessage } from "@/components/Forms/ErrorMessage";
import { logIn } from "@/routes/auth";
import { Paths } from "@/routes/paths";
import { PageModule } from "@/routes/types";
import { match } from "ts-pattern";

export default { name: "SignUpWithEmailPage", loader: Pages.emptyLoader, Page } as PageModule;

type PageState = "form" | "code-verification";

//
// The page is split into two states: "form" and "code-verification".
//
// On the "form" state, the user is asked to fill in their email, name, and password.
// When the user submits the form, an email with a verification code is sent to the
// user's email address. The page then transitions to the "code-verification" state.
//
// On the "code-verification" state, the user is asked to enter the verification code
// they received in their email. When the user submits the form, the account is created
// and the user is logged in.
//

function Page() {
  const inviteToken = new URLSearchParams(window.location.search).get("invite_token");
  const redirectToParam = new URLSearchParams(window.location.search).get("redirect_to");
  const [pageState, setPageState] = React.useState<PageState>("form");
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const form = Forms.useForm({
    fields: {
      inviteToken,
      email: "",
      name: "",
      password: "",
      confirmPassword: "",
      code: "",
    },
    validate: (addError) => {
      if (form.values.password !== form.values.confirmPassword) {
        addError("confirmPassword", "Passwords do not match");
      }
    },
    submit: async () => {
      if (pageState === "form") {
        setSubmitError(null);

        await Api.createEmailActivationCode({ email: form.values.email });
        setPageState("code-verification");
      } else {
        const result = await Api.createAccount({
          inviteToken: form.values.inviteToken,
          code: form.values.code,
          email: form.values.email,
          fullName: form.values.name,
          password: form.values.password,
        });

        const redirectTo = buildPostSignupRedirect({
          inviteToken: form.values.inviteToken,
          companyId: result.company?.id,
          joinErrorDetails: result.joinErrorDetails,
          redirectToParam,
        });

        if (result.error) {
          console.warn("createAccount invite join warning", result.error);
        }

        await logIn(form.values.email, form.values.password, { redirectTo });
      }
    },
    onError: (error) => {
      const data = error.response?.data as { error?: string; message?: string } | undefined;
      const message = data?.message;

      if (message) {
        setSubmitError(message);
      } else {
        setSubmitError("There was an unexpected error. Please try again later.");
      }
    },
  });

  return match(pageState)
    .with("form", () => <Form form={form} submitError={submitError} />)
    .with("code-verification", () => <CodeVerification form={form} />)
    .exhaustive();
}

function Form({ form, submitError }: { form: ReturnType<typeof Forms.useForm>; submitError: string | null }) {
  const validation = validateForm(form);

  return (
    <Pages.Page title={["Sign Up"]} testId="sign-up-page">
      <Paper.Root size="tiny">
        <Paper.Body className="h-dvh sm:h-auto">
          <div className="py-8 sm:px-4 sm:py-4">
            <OperatelyLogo width="40px" height="40px" />
            <h1 className="text-2xl font-bold mt-4">Sign up for Operately</h1>
            <p className="text-content-dimmed mb-8">Use your work email — keep work and life separate.</p>

            <Forms.Form form={form}>
              {submitError && (
                <div className="mb-4" role="alert">
                  <ErrorMessage error={submitError} />
                </div>
              )}

              <Forms.FieldGroup>
                <Forms.TextInput
                  field={"email"}
                  label="Work Email"
                  placeholder="name@company.com"
                  required
                  okSign={validation.email}
                />

                <Forms.TextInput
                  field={"name"}
                  label="Full Name"
                  placeholder="Enter your full name"
                  required
                  okSign={validation.name}
                />

                <Forms.PasswordInput
                  field={"password"}
                  label="Password"
                  minLength={12}
                  placeholder="At least 12 characters"
                  required
                  noAutofill
                  okSign={validation.password}
                />

                <PasswordStrength password={form.values.password} />

                <Forms.PasswordInput
                  field={"confirmPassword"}
                  label="Confirm Password"
                  minLength={12}
                  placeholder="At least 12 characters"
                  required
                  noAutofill
                  okSign={validation.confirmPassword}
                />
              </Forms.FieldGroup>

              <div className="my-6">
                <Forms.Submit
                  saveText={!validation.isValid ? "Please fill in all fields" : "Continue ->"}
                  className="w-full py-2 px-4"
                />
              </div>

              <TosAndPrivacyPolicy />
            </Forms.Form>
          </div>
        </Paper.Body>
        <WhatHappensNext />
      </Paper.Root>
    </Pages.Page>
  );
}

function WhatHappensNext() {
  return (
    <div className="my-8 text-center px-20">
      <span className="font-bold">What happens next?</span> Operately will send you a code to verify your email address.
    </div>
  );
}

function CodeVerification({ form }: { form: ReturnType<typeof Forms.useForm> }) {
  return (
    <Pages.Page title={["Sign Up"]} testId="sign-up-page">
      <Paper.Root size="tiny">
        <Paper.Body className="h-dvh sm:h-auto">
          <Forms.Form form={form}>
            <div className="py-8 sm:px-4 sm:py-4 flex flex-col items-center text-center">
              <OperatelyLogo width="32px" height="32px" />
              <h1 className="text-3xl font-bold mt-4 mb-4">Check your email for a code</h1>
              <CodeMessage />

              <div className="flex flex-col items-center">
                <CodeInput field={"code"} />
                <Forms.Submit saveText="Continue ->" className="w-60" />
              </div>

              <div className="mt-8 text-center text-sm">Can’t find your code? Check your spam folder.</div>
            </div>
          </Forms.Form>
        </Paper.Body>
      </Paper.Root>
    </Pages.Page>
  );
}

function CodeMessage() {
  const [email] = Forms.useFieldValue<string>("email");

  return (
    <p className="text-content-dimmed mb-8">
      We've sent you a 6-character code to <span className="font-bold">{email}</span>. The code expires in 5 minutes, so
      please enter it soon.
    </p>
  );
}

function CodeInput({ field }: { field: string }) {
  const [value, setValue] = useFieldValue(field);

  return (
    <input
      name="code"
      autoFocus
      data-test-id="code"
      className={
        "text-4xl border border-surface-outline rounded-lg px-3 py-1.5 text-center placeholder-content-subtle w-60 bg-surface-base text-content-base"
      }
      type="text"
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

interface FormValidation {
  email: boolean;
  name: boolean;
  password: boolean;
  confirmPassword: boolean;
  isValid: boolean;
}

function validateForm(form: any): FormValidation {
  const email = form.values.email.trim() !== "" && form.values.email.match(/.+@.+\..+/);
  const name = form.values.name.trim() !== "";
  const password = validatePassword(form.values.password).isValid;
  const confirmPassword = password && form.values.password === form.values.confirmPassword;

  const isValid = email && name && password && confirmPassword;

  return { email, name, password, confirmPassword, isValid };
}

function buildPostSignupRedirect({
  inviteToken,
  companyId,
  joinErrorDetails,
  redirectToParam,
}: {
  inviteToken: string | null;
  companyId?: string | null;
  joinErrorDetails?: unknown;
  redirectToParam: string | null;
}) {
  const joinLimitError = Billing.extractLimitErrorDetails(joinErrorDetails);

  if (joinLimitError?.code === "member_count_limit_exceeded" && inviteToken) {
    return Paths.inviteJoinFullPath(inviteToken);
  }

  if (companyId) {
    return `/${companyId}`;
  }

  return redirectToParam || "/";
}
